import React, { useState } from "react";
import { X } from "lucide-react";
import api from "../../../lib/api";
import { logger } from "../../../lib/logger";
import { toast } from "sonner";
import { inp, lbl } from "../shared";

export const newKey = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
const MAX = 10;

const AccordRow = ({ ac, i, onChange, onRemove }) => (
  <div className="border border-[#262421] p-3 space-y-2" data-testid={`accord-row-${i}`}>
    <div className="flex items-center gap-2">
      <input type="color" value={ac.color || "#C49A6C"} onChange={(e) => onChange("color", e.target.value)} data-testid={`accord-color-${i}`} className="w-10 h-10 bg-transparent border border-[#262421] cursor-pointer shrink-0" />
      <div className="flex items-center gap-1">
        <input type="number" min="1" max="100" value={ac.pct ?? ""} onChange={(e) => onChange("pct", e.target.value === "" ? null : parseInt(e.target.value, 10))} placeholder="%" data-testid={`accord-pct-${i}`} className={inp + " w-20 !mt-0"} />
        <span className="text-[10px] uppercase tracking-widest text-[#6E685F]">%</span>
      </div>
      <button type="button" onClick={onRemove} data-testid={`accord-remove-${i}`} className="ms-auto text-[#c88] hover:text-[#e99] shrink-0 p-1"><X size={18} /></button>
    </div>
    <input value={ac.name_en || ""} onChange={(e) => onChange("name_en", e.target.value)} placeholder="Name (EN) — e.g. Coffee" data-testid={`accord-name-en-${i}`} className={inp + " w-full !mt-0"} />
    <input dir="rtl" value={ac.name_ar || ""} onChange={(e) => onChange("name_ar", e.target.value)} placeholder="الاسم (عربي) — مثال: قهوة" data-testid={`accord-name-ar-${i}`} className={inp + " w-full !mt-0"} />
  </div>
);

export const AccordsEditor = ({ p, set }) => {
  const [suggesting, setSuggesting] = useState(false);
  const accords = p.accords || [];

  const suggest = async () => {
    setSuggesting(true);
    try {
      const { data } = await api.post("/admin/suggest-accords", {
        name: p.name_en || p.name_ar, description: p.description_en || p.description_ar, notes: p.notes_en || [],
      });
      if (data.accords?.length) {
        set("accords", data.accords.map((a) => ({ ...a, _k: newKey() })));
        toast.success("AI suggested accords added");
      }
    } catch (e) {
      logger.error("suggest accords failed", e);
      toast.error("Could not generate accords");
    } finally {
      setSuggesting(false);
    }
  };
  const add = () => { if (accords.length < MAX) set("accords", [...accords, { _k: newKey(), name_en: "", name_ar: "", color: "#C49A6C", pct: 50 }]); };
  const update = (i, k, v) => set("accords", accords.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)));
  const remove = (i) => set("accords", accords.filter((_, idx) => idx !== i));

  return (
    <div className="md:col-span-2 border border-[#262421] p-4 mt-1">
      <div className="flex items-center justify-between mb-3">
        <label className={lbl + " !mb-0"}>Main Accords · النوتات الرئيسية (bar length = %)</label>
        <div className="flex items-center gap-2">
          <button type="button" data-testid="accord-ai-suggest" onClick={suggest} disabled={suggesting}
            className="text-[10px] uppercase tracking-widest border border-[#8A6BC4] text-[#c3a6ef] px-3 py-1.5 hover:bg-[#8A6BC4] hover:text-white transition-colors disabled:opacity-40">
            {suggesting ? "✨ …" : "✨ AI Suggest"}
          </button>
          <button type="button" data-testid="accord-add" onClick={add} disabled={accords.length >= MAX}
            className="text-[10px] uppercase tracking-widest border border-[#C49A6C] text-[#C49A6C] px-3 py-1.5 hover:bg-[#C49A6C] hover:text-[#0B0A08] transition-colors disabled:opacity-40">
            + Add ({accords.length}/{MAX})
          </button>
        </div>
      </div>
      <div className="space-y-2">
        {accords.map((ac, i) => (
          <AccordRow key={ac._k || `${ac.name_en}-${i}`} ac={ac} i={i} onChange={(k, v) => update(i, k, v)} onRemove={() => remove(i)} />
        ))}
        {accords.length === 0 && <p className="text-xs text-[#6E685F]">لا توجد نوتات بعد. اضغط "Add". فقط الصفوف المكتملة (اسم + نسبة) تظهر للعميل.</p>}
      </div>
    </div>
  );
};
